import { Permissions, type PermissionCode } from '@/models/permission';

/**
 * What each page requires. Referenced by name from the route gates and from the
 * navigation trees (`components/sidebar/pages.tsx`), so a nav entry and its route
 * can never gate on different codes.
 *
 * Arrays are `oneOf` — holding any one of the codes opens the page.
 */
export const PagePermissions = {
  players: [Permissions.players.read],
  catalogItems: [Permissions.items.read],
  // The Bundles page is the items list filtered to type=bundle, so it reads the items
  // endpoint. `bundles:read` is reserved and governs no endpoint — gating on it would
  // 403 holders of items:read on data the backend serves.
  catalogBundles: [Permissions.items.read],
  catalogCurrency: [Permissions.items.read],
  seasonPasses: [Permissions.seasonpass.read],
  gifts: [Permissions.gifts.read],
  // Not translations:read, which no route enforces. The page's blocking loader fetches
  // GET /v1/languages, and every Role holding languages:read also holds items:read.
  translations: [Permissions.languages.read],
  transactionsStore: [Permissions.store_transactions.read],
  transactionsInventory: [Permissions.inventory_transactions.read],
  snapshots: [Permissions.store.read],
  // Its sections: Naming writes the Sandbox, Supported Languages writes the language
  // list, and Export only reads. sandboxes:read is here on purpose — it is the one
  // page a Sandbox reader can use, so the index lands them here instead of bouncing
  // them back to the Sandbox list.
  settingsGeneral: [
    Permissions.sandboxes.read,
    Permissions.sandboxes.edit,
    Permissions.languages.edit,
  ],
  // The dedicated import codes, not create: bulk import is its own action
  // (BR-RBAC-36), and each wizard route re-gates on its own resource.
  settingsImport: [
    Permissions.items.import,
    Permissions.gifts.import,
    Permissions.seasonpass.import,
    Permissions.store.import,
  ],
} satisfies Record<string, PermissionCode[]>;
